import React, { useState, useRef, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Spin, Popover } from "antd";
import moment from "moment";
import {
  SearchOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  CloseCircleFilled,
  ScheduleOutlined,
  IdcardOutlined,
  PieChartOutlined,
  GiftOutlined,
  FileProtectOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  StarOutlined,
  StarFilled,
} from "@ant-design/icons";
import useGlobalSearch, { DIAGNOSTIC_QUERY_HINT } from "../../hooks/useGlobalSearch";
import { getResource } from "../../util/getResourcePermission";
import { getRoles } from "../../util/getRoles";
import config from "../../permission/config.json";
import { getRecentSearches, addRecentSearch } from "./recentSearchStore";
import { getRecentlyViewed, addRecentlyViewed, clearRecentlyViewed } from "./recentlyViewedStore";
import { getFavorites, toggleFavorite } from "./favoritesStore";
import { isDiagnosticTipEligible, recordDiagnosticTipView, dismissDiagnosticTip } from "./diagnosticTipStore";
import { ROLE_SUGGESTED_SEARCHES, getSuggestedSearchesForViewer } from "./roleSuggestedSearches";
import { SEARCH_PROMPTS, getSuggestedPromptsForViewer } from "./searchPrompts";
import { OPERATOR_KEYS, parseOperatorTokens, removeOperatorToken, getOperatorValueSuggestions } from "./searchOperators";
import { HighlightText } from "./highlightMatch";
import "./GlobalSearch.css";

// Kept short and role-agnostic on purpose - a longer Admin-specific hint was
// tried here and visibly truncated/overflowed against the Ctrl K badge at
// normal input widths (placeholder text has no safe way to wrap or shrink).
// The diagnostic-FAQ hint now lives in the empty-state "Try asking a
// specific question" examples below instead, which have real layout room.
const PLACEHOLDER = "Search...";

// Marker text inside a fillOnly prompt's `template` (see searchPrompts.js)
// that gets selected/highlighted so typing a real name immediately replaces it.
const TEMPLATE_PLACEHOLDER = "[employee name]";

// Backend sends raw ISO timestamps and enum-style status strings
// (e.g. "half_day_absent") - admins shouldn't see either as-is.
const formatDate = (value) => (value && moment(value).isValid() ? moment(value).format("DD MMM YYYY") : value);
const formatStatus = (value) =>
  value ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : value;

// Admin/HR see everyone company-wide; Reporting Manager/Assistance Reporting
// Manager see their own team - same ladder server/helpers/roleScopeHelper.js
// already uses to scope the actual search results below.
const ADMIN_SCOPE_ROLES = ["Admin", "HR", "Reporting Manager", "Assistance Reporting Manager"];

// Shown before the user types anything - direct jumps to the pages Global
// Search otherwise only surfaces records from. Purely client-side, no API call.
// `resource` matches the same permission check newRoutes.js/SettingsDropdown.js
// already use to gate that page. That alone isn't always precise enough, though:
// ATTENDANCE_REPORT and SHIFT are also granted to plain employees for their own
// read-only "my attendance"/"my shift" views elsewhere in the app, so relying on
// the resource flag alone let a plain User see links straight into the
// COMPANY-WIDE admin pages. `roles`, where set, is an extra hard gate on top.
const QUICK_LINKS = [
  { label: "Employees", route: "/viewEmployeeDetails", icon: <UserOutlined />, color: "#7C3AED", resource: config.RESOURCES.EMPLOYEE_DETAILS },
  { label: "Leave Approvals", route: "/admin-leave-approval", icon: <CalendarOutlined />, color: "#16A34A", resource: config.RESOURCES.ADMIN_LEAVE_MANAGEMENT },
  { label: "Attendance Register", route: "/attendence-reports/attendence-register", icon: <ClockCircleOutlined />, color: "#2563EB", resource: config.RESOURCES.ATTENDANCE_REPORT, roles: ADMIN_SCOPE_ROLES },
  { label: "Work Week & Shifts", route: "/admin-shift", icon: <ScheduleOutlined />, color: "#D97706", resource: config.RESOURCES.SHIFT, roles: ["Admin", "HR"] },
  { label: "General Settings", route: "/admin-attendance-settings", icon: <SettingOutlined />, color: "#6B7280", resource: config.RESOURCES.ADMIN_REGULARIZATION, roles: ["Admin", "HR"] },
  { label: "System Settings", route: "/admin/settings", icon: <SettingOutlined />, color: "#6B7280", resource: config.RESOURCES.SETTINGS, roles: ["Admin", "HR"] },
  // Self-service links below - available to every role, not just Admin/HR.
  // Each destination was verified (not assumed) to actually be a "my own data"
  // page with no cross-employee picker, by reading the real component:
  // - My Profile / Book Meeting Room: no resource gate needed, inherently self.
  // - My Leave -> /leave-application (LeaveApplication.js, resource LEAVE):
  //   this replaces an earlier wrong guess of /user-leave-approve, which is
  //   actually the Reporting Manager/ARM TEAM approval page (sets
  //   reporting_manager_id/assistant_reporting_manager_id from the viewer) -
  //   not a personal leave page at all. LeaveApplication.js has no such
  //   manager-id logic and no employee picker, confirmed self-scoped.
  // - My Attendance -> UserAttendenceRegister.js: confirmed no employee
  //   picker, distinct component from the admin AttendenceRegister.js despite
  //   the similar-looking path.
  // - Policies: policiesList.js gates its Edit/Create/Delete buttons inline
  //   via getResource(POLICIES, ["update"/"create"/"delete"]), so a
  //   read-only viewer only ever sees a plain list - confirmed safe.
  // Leave Balance and Holidays are NOT in this self-service group - both
  // were checked and found to have no safe self-view (see `roles` below).
  { label: "My Profile", route: "/my-profile", icon: <IdcardOutlined />, color: "#0D9488", resource: null },
  { label: "My Leave", route: "/leave-application", icon: <CalendarOutlined />, color: "#059669", resource: config.RESOURCES.LEAVE },
  { label: "My Attendance", route: "/user-reports/attendence-reports/attendence-register", icon: <ClockCircleOutlined />, color: "#0891B2", resource: config.RESOURCES.ATTENDANCE_REPORT },
  { label: "Policies", route: "/policies", icon: <FileProtectOutlined />, color: "#4F46E5", resource: config.RESOURCES.POLICIES },
  { label: "Book Meeting Room", route: "/book-meeting-room", icon: <VideoCameraOutlined />, color: "#F59E0B", resource: null },
  // Below: resource flag alone isn't enough, same reasoning as the admin
  // links above it.
  // - Leave Balance -> LeaveBalance.js: unconditional company-wide "Employee"
  //   dropdown with no internal role check, and its fetch only scopes by
  //   empCode when one is explicitly picked - with nothing picked (the
  //   default state on landing) it returns an unfiltered, not self-scoped,
  //   result set. No safe self-view exists to redirect to instead.
  // - Holidays -> Holidays.js: the only /holidays route IS the admin CRUD
  //   page (toggle Optional Holiday, Actions column to edit/delete) - a
  //   plain User can and did accidentally edit a holiday through this link.
  //   No separate read-only holiday list route exists (the dashboard's own
  //   "Upcoming Holidays" widget is the real self-service view, not a page).
  { label: "Leave Balance", route: "/leave-reports/leave-balance", icon: <PieChartOutlined />, color: "#0284C7", resource: config.RESOURCES.LEAVE_REPORT, roles: ADMIN_SCOPE_ROLES },
  { label: "Holidays", route: "/holidays", icon: <GiftOutlined />, color: "#DB2777", resource: config.RESOURCES.HOLIDAYS, roles: ["Admin", "HR"] },
];

// One entry per result category. `renderLabel`/`renderSubtitle` are the only
// places that read fields off an item - if the backend finalizes field names
// slightly differently, only these need to change, not the rendering below.
const CATEGORY_CONFIG = {
  employees: {
    title: "Employees",
    icon: <UserOutlined />,
    color: "#7C3AED",
    renderLabel: (item) => item.name || item.employeeId || item.email || "Unnamed employee",
    renderSubtitle: (item) => item.email,
  },
  // One row per matching employee (not per application) - the backend rolls
  // up every leave application into a Pending/Total count per employee.
  leaveRequests: {
    title: "Leave Requests",
    icon: <CalendarOutlined />,
    color: "#16A34A",
    renderLabel: (item) => item.employeeName || "Leave request",
    renderSubtitle: (item) =>
      item.pendingCount > 0
        ? `${item.pendingCount} pending · ${item.totalCount} total`
        : `${item.totalCount} total leave request${item.totalCount === 1 ? "" : "s"}`,
  },
  // One row per matching employee (not per day) - rolled up into a
  // Present/Absent count over the last 90 days, plus their latest status.
  attendanceRecords: {
    title: "Attendance",
    icon: <ClockCircleOutlined />,
    color: "#2563EB",
    renderLabel: (item) => item.employeeName || "Attendance",
    renderSubtitle: (item) =>
      `Last: ${formatStatus(item.lastStatus)} (${formatDate(item.lastDate)}) · ${item.presentCount} present, ${item.absentCount} absent`,
  },
  holidays: {
    title: "Holidays",
    icon: <GiftOutlined />,
    color: "#DB2777",
    renderLabel: (item) => item.name,
    renderSubtitle: (item) => formatDate(item.date),
  },
  policies: {
    title: "Policies",
    icon: <FileProtectOutlined />,
    color: "#4F46E5",
    renderLabel: (item) => item.title,
  },
  settings: {
    title: "Settings",
    icon: <SettingOutlined />,
    color: "#6B7280",
    renderLabel: (item) => item.label,
    renderSubtitle: (item) => item.sectionLabel,
  },
  // Static, keyword-matched command shortcuts (e.g. "apply leave", "book room") -
  // see actionsSearchIndex.js. Shown first since an action is often what the
  // searcher actually wants, ahead of record results.
  actions: {
    title: "Actions",
    icon: <ThunderboltOutlined />,
    color: "#CA8A04",
    renderLabel: (item) => item.label,
    renderSubtitle: () => "Quick action",
  },
  // Static, keyword-matched HRMS module shortcuts (e.g. "payroll", "onboarding") -
  // see moduleSearchIndex.js. Same purely client-side pattern as "actions"/"settings".
  modules: {
    title: "HRMS Modules",
    icon: <AppstoreOutlined />,
    color: "#0E7490",
    renderLabel: (item) => item.label,
  },
};

const CATEGORY_ORDER = [
  "actions",
  "employees",
  "leaveRequests",
  "attendanceRecords",
  "holidays",
  "policies",
  "settings",
  "modules",
];

// Recently Viewed only honestly tracks what Global Search knows a user opened
// by clicking an actual RECORD result row - not quick links/actions/modules/
// settings, which aren't "a record" in the same sense. Kept as a subset of
// handleSelect's own switch below.
const RECENTLY_VIEWED_CATEGORIES = ["employees", "leaveRequests", "attendanceRecords", "holidays", "policies"];

// Subtitle highlighting is only safe where the subtitle itself is a plain
// free-text-matched field (an employee's email) - every other category's
// subtitle is a derived/computed string (counts, formatted dates, section
// labels) that the query never matches against directly.
const SUBTITLE_HIGHLIGHT_CATEGORIES = ["employees"];

// Flattened across all role buckets, purely so resolveRecentIcon can look a
// Suggested-for-You row's icon/color back up by label for the Recent list -
// not used for the role gating itself (getSuggestedSearchesForViewer already
// picks the right bucket and filters it for that).
const ALL_SUGGESTED_ENTRIES = Object.values(ROLE_SUGGESTED_SEARCHES).flat();

// Same purely-cosmetic flattening as ALL_SUGGESTED_ENTRIES above, just for the
// "Suggested Queries" prompt rows (resolveRecentIcon's "prompt:<id>" branch) -
// not used for role gating, which getSuggestedPromptsForViewer already does.
const ALL_PROMPT_ENTRIES = Object.values(SEARCH_PROMPTS).flat();

const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "");

const getCompanySlug = () => localStorage.getItem("companyDomain");

const GlobalSearch = () => {
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const { results, loading, error, diagnosticAnswer } = useGlobalSearch(query);

  // Operator chips (e.g. "status:pending") parsed live out of the typed
  // string - purely synchronous regex work (see searchOperators.js), cheap
  // enough to run on every keystroke with no extra debounce. The backend
  // still just receives the raw `query` string unchanged via useGlobalSearch.
  const { chips: operatorChips, freeText: queryFreeText } = useMemo(() => parseOperatorTokens(query), [query]);

  const handleRemoveChip = (chip) => {
    setQuery(removeOperatorToken(query, chip));
    inputRef.current?.focus();
  };

  const handleClearAllChips = () => {
    setQuery(parseOperatorTokens(query).freeText);
    inputRef.current?.focus();
  };

  // Detects an unfinished "key:partial" token sitting at the very end of the
  // input (the token the user is actively typing right now) so we can offer
  // value autocomplete for it. Only keys in OPERATOR_KEYS count - anything
  // else is left alone, same inert-passthrough rule as parseOperatorTokens.
  const trailingOperatorToken = useMemo(() => {
    const match = query.match(/(\w+):([^\s]*)$/);
    if (!match) return null;
    const key = match[1].toLowerCase();
    if (!OPERATOR_KEYS.includes(key)) return null;
    return { key, partial: match[2], raw: match[0] };
  }, [query]);

  const operatorSuggestions = useMemo(() => {
    if (!trailingOperatorToken) return [];
    return getOperatorValueSuggestions(trailingOperatorToken.key, trailingOperatorToken.partial);
  }, [trailingOperatorToken]);

  // Separate index for the small operator-suggestion list so its keyboard
  // nav never touches `activeIndex` (the main results list's own cursor).
  const [operatorActiveIndex, setOperatorActiveIndex] = useState(-1);
  useEffect(() => {
    setOperatorActiveIndex(-1);
  }, [operatorSuggestions.length, trailingOperatorToken?.key, trailingOperatorToken?.partial]);

  const handleSelectOperatorSuggestion = (entry) => {
    if (!trailingOperatorToken) return;
    const value = typeof entry === "string" ? entry : entry.value;
    const prefix = query.slice(0, query.length - trailingOperatorToken.raw.length);
    // Collapse any accidental double space from this concatenation (e.g. QA
    // found "pending  department:hr" - two spaces - could result depending on
    // exactly what whitespace `prefix` already ended with) - defensive, not
    // tied to one specific root cause, so it holds regardless of how `prefix`
    // got here.
    setQuery(`${prefix}${trailingOperatorToken.key}:${value} `.replace(/\s{2,}/g, " "));
    setOperatorActiveIndex(-1);
    inputRef.current?.focus();
  };

  // Guards against the browser firing a synthetic `mouseenter` on whatever
  // row ends up sitting under an already-stationary cursor whenever the
  // dropdown opens or its rows reshuffle (new results arriving, query
  // changing length, etc.) - no real pointer movement involved, but the
  // row still lights up as if hovered. Real mouse hovering is only honored
  // once an actual `mousemove` is seen over the list after the last reshape.
  const mouseArmedRef = useRef(false);
  const armMouse = () => {
    mouseArmedRef.current = true;
  };
  const disarmMouse = () => {
    mouseArmedRef.current = false;
  };
  const handleRowMouseEnter = (index) => {
    if (mouseArmedRef.current) setActiveIndex(index);
  };
  // Every reshape of the row list (new results landing, or the quick-links/
  // recent list swapping in/out) must disarm hovering again.
  useEffect(disarmMouse, [results, isOpen, diagnosticAnswer]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Global ⌘K / Ctrl+K shortcut to jump into the search box from anywhere.
  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  const closeDropdown = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  // "Recent" group - last 5 results this browser's logged-in user selected,
  // most-recent-first, per client/src/components/GlobalSearch/recentSearchStore.js.
  // Re-read on focus so a selection made elsewhere (or in a previous session)
  // shows up without needing a remount.
  const [recentSearches, setRecentSearches] = useState(() => getRecentSearches());

  // Stores a flattened, minimal row (not the raw API item) so it can be
  // re-rendered and re-navigated to later without needing the original lookups.
  // `route` is the already-resolved destination (companySlug baked in), and
  // `iconCategory` is a lookup key resolved back to an icon/color at render time
  // (see resolveRecentIcon) rather than a React node, since icons aren't
  // serializable to localStorage.
  const recordRecent = ({ id, label, subtitle, route, iconCategory }) => {
    addRecentSearch({ id, label, subtitle, route, iconCategory });
    setRecentSearches(getRecentSearches());
  };

  // "Recently Viewed" group - records the user actually opened by clicking a
  // search RESULT ROW (see RECENTLY_VIEWED_CATEGORIES), per
  // client/src/components/GlobalSearch/recentlyViewedStore.js. Separate list
  // and separate storage key from "Recent" above - re-read on focus for the
  // same reason recentSearches is.
  const [recentlyViewed, setRecentlyViewed] = useState(() => getRecentlyViewed());

  const recordRecentlyViewed = ({ id, label, subtitle, route, iconCategory, categoryKey }) => {
    addRecentlyViewed({ id, label, subtitle, route, iconCategory, categoryKey });
    setRecentlyViewed(getRecentlyViewed());
  };

  const handleClearRecentlyViewed = () => {
    clearRecentlyViewed();
    setRecentlyViewed([]);
  };

  // Favorites - user-curated pins of QUICK_LINKS entries, by label (see
  // favoritesStore.js). Re-filtered against permissions on every read via
  // visibleFavorites below, never trusted blindly off storage.
  const [favorites, setFavorites] = useState(() => getFavorites());

  const handleToggleFavorite = (e, label) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites(toggleFavorite(label));
  };

  const handleSelect = (categoryKey, item) => {
    const companySlug = getCompanySlug();
    const config_ = CATEGORY_CONFIG[categoryKey];
    let route = null;
    let label = config_?.renderLabel?.(item);
    let subtitle = config_?.renderSubtitle?.(item);

    switch (categoryKey) {
      case "employees":
        // Unlike leaveRequests/attendanceRecords below, this is a real
        // page-level limitation, not an oversight: EmpDetails.js (the
        // component behind /viewEmployeeDetails) was read in full and has
        // NO query-param handling at all (no useLocation/location.search,
        // no URLSearchParams) - its employee filter only ever comes from
        // `filterData` state set by the in-page Filter UI. There is no
        // `?employee_id=`/`?search=` (or any other param) for it to read,
        // so there is nothing safe to wire up here without first adding
        // that support to EmpDetails.js itself (out of scope for this fix).
        route = `/${companySlug}/viewEmployeeDetails`;
        break;
      case "leaveRequests": {
        // AdminLeaveApprovel.js seeds its real "Employee" filter
        // (filterData.employee -> reqBody.employee_id) from `employee_id`,
        // and its status dropdown from `leave_status` - both auto-apply on
        // load, no free-text search box involved.
        const leaveParams = new URLSearchParams();
        if (item._id) leaveParams.set("employee_id", item._id);
        if (item.pendingCount > 0) leaveParams.set("leave_status", "Pending");
        route = `/${companySlug}/admin-leave-approval?${leaveParams.toString()}`;
        break;
      }
      case "attendanceRecords": {
        // AttendenceRegister.js's Employee filter needs the employee's Mongo
        // _id (not a name string) - the rollup item's `_id` is exactly that,
        // since the backend groups attendance records by employee_id.
        const attendanceParams = new URLSearchParams();
        if (item._id) attendanceParams.set("employee_id", item._id);
        route = `/${companySlug}/attendence-reports/attendence-register?${attendanceParams.toString()}`;
        break;
      }
      case "holidays":
        route = `/${companySlug}/holidays`;
        break;
      case "policies":
        route = `/${companySlug}/policies`;
        break;
      case "settings":
        route = `/${companySlug}${item.route}?tab=${item.tabKey}`;
        break;
      case "actions":
      case "modules":
        route = `/${companySlug}${item.route}`;
        break;
      default:
        break;
    }

    if (route) {
      recordRecent({ id: route, label, subtitle, route, iconCategory: categoryKey });
      if (RECENTLY_VIEWED_CATEGORIES.includes(categoryKey)) {
        recordRecentlyViewed({ id: route, label, subtitle, route, iconCategory: categoryKey, categoryKey });
      }
      history.push(route);
    }
    setQuery("");
    closeDropdown();
  };

  // Admin diagnostic Answer row (see useGlobalSearch's diagnosticAnswer) -
  // not a navigable record row itself (it's a text answer), just the same
  // employee_id deep-link pattern handleSelect already uses for
  // attendanceRecords above, reused verbatim for the "View {employee}'s
  // attendance" link.
  const handleSelectDiagnosticAnswer = (answer) => {
    if (!answer?.employee?._id) return;
    const companySlug = getCompanySlug();
    const attendanceParams = new URLSearchParams();
    attendanceParams.set("employee_id", answer.employee._id);
    const route = `/${companySlug}/attendence-reports/attendence-register?${attendanceParams.toString()}`;
    // `answer.answer` is the raw diagnostic sentence from the backend (e.g.
    // "...manually edited by 69552b920b7e6c003f1336b1") - it's meant to be
    // read once as the Answer card's own body text, not reused verbatim as a
    // Recent row's subtitle: it can run arbitrarily long (overflows/clips
    // mid-word against the row's single-line ellipsis) and can contain a raw
    // Mongo ObjectId with no resolved name. A short, fixed, generic subtitle
    // avoids both failure modes without trying to parse/strip the sentence.
    const recentEntry = {
      id: route,
      label: `${answer.employee.name}'s attendance`,
      subtitle: "Diagnostic answer",
      route,
      iconCategory: "diagnosticAnswer",
    };
    recordRecent(recentEntry);
    // This click navigates straight to that employee's attendance register,
    // same destination/shape as an attendanceRecords row's own click - so it
    // honestly belongs in "Recently Viewed" too (RECENTLY_VIEWED_CATEGORIES),
    // not just "Recent". Without this, every diagnostic-answer click only
    // ever landed in "Recent", and Recently Viewed stayed empty/never showed
    // as a distinct section unless a separate record row was also clicked.
    recordRecentlyViewed({ ...recentEntry, categoryKey: "attendanceRecords" });
    history.push(route);
    setQuery("");
    closeDropdown();
  };

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const showDropdown = isOpen;

  // Only suggest pages this viewer can actually open - same permission check
  // newRoutes.js/SettingsDropdown.js already gate the real pages behind, plus
  // an explicit role check where the resource flag alone isn't precise enough
  // (see QUICK_LINKS comment above).
  const visibleQuickLinks = useMemo(
    () =>
      QUICK_LINKS.filter(
        (link) => (!link.resource || getResource(link.resource, ["read"])) && (!link.roles || getRoles(link.roles))
      ),
    []
  );

  // Favorites, resolved back from QUICK_LINKS by label and re-filtered
  // through the SAME permission check as visibleQuickLinks on every read -
  // a favorite whose underlying resource/role access was later revoked must
  // not render, so the stored label list is never trusted blindly.
  const visibleFavorites = useMemo(
    () =>
      favorites
        .map((label) => QUICK_LINKS.find((link) => link.label === label))
        .filter(
          (link) => link && (!link.resource || getResource(link.resource, ["read"])) && (!link.roles || getRoles(link.roles))
        ),
    [favorites]
  );

  // Role-curated subset of the same already-permission-checked destinations
  // (see roleSuggestedSearches.js) - a different VIEW over Quick Links, not a
  // new source, so overlap with visibleQuickLinks for a given viewer is
  // expected and fine.
  const suggestedSearches = useMemo(() => getSuggestedSearchesForViewer(), []);

  // Static, no-AI "Suggested Queries" (see searchPrompts.js) - a third, separate
  // role-curated view over the same already-permission-checked destinations,
  // same pattern as suggestedSearches above.
  const suggestedPrompts = useMemo(() => getSuggestedPromptsForViewer(), []);

  // Pinned "Try asking..." tip banner (see diagnosticTipStore.js) - a small,
  // always-visible-on-open teaching moment for the Admin diagnostic FAQ
  // feature, since the same examples buried several groups down inside
  // "Suggested Queries" were confirmed (live screenshot) to require
  // scrolling before an Admin ever sees them. `diagnosticTipEntry` reuses
  // the SAME already-role-filtered suggestedPrompts list above - it's only
  // ever non-empty for an Admin viewer (only the Admin bucket in
  // searchPrompts.js has fillOnly entries) - no separate role check or
  // second copy of the prompt list.
  const diagnosticTipEntry = useMemo(() => suggestedPrompts.find((entry) => entry.fillOnly), [suggestedPrompts]);
  // Every example question (not just one) - shown as a menu inside the
  // badge's popover below, so clicking the badge itself only ever shows
  // info, never silently drops text into the search box on its own.
  const diagnosticTipExamples = useMemo(() => suggestedPrompts.filter((entry) => entry.fillOnly), [suggestedPrompts]);
  const [diagnosticBadgePopoverOpen, setDiagnosticBadgePopoverOpen] = useState(false);
  const [diagnosticTipEligible, setDiagnosticTipEligible] = useState(() => isDiagnosticTipEligible());
  const diagnosticTipCountedRef = useRef(false);

  // Flat list [{categoryKey, item}] in display order, so arrow-key navigation
  // can move across category boundaries with a single index. While the query
  // is empty, the flat list is Recent (if any), then Suggested for You, then
  // Quick Links.
  const flatResults = useMemo(() => {
    if (!isSearching) {
      // Order mirrors the empty-query dropdown below: Favorites, Recent,
      // Recently Viewed, Suggested for You, Suggested Queries, Quick Links.
      return [
        ...visibleFavorites.map((item) => ({ categoryKey: "favorites", item })),
        ...recentSearches.map((item) => ({ categoryKey: "recent", item })),
        ...recentlyViewed.map((item) => ({ categoryKey: "recentlyViewed", item })),
        ...suggestedSearches.map((item) => ({ categoryKey: "suggested", item })),
        ...suggestedPrompts.map((item) => ({ categoryKey: "prompts", item })),
        ...visibleQuickLinks.map((item) => ({ categoryKey: "quickLinks", item })),
      ];
    }
    const flat = [];
    // "Answer" sits above every other group - it's the most direct hit when
    // it matched (see diagnosticSearch.js's own doc comment / the plan doc).
    if (diagnosticAnswer) flat.push({ categoryKey: "diagnosticAnswer", item: diagnosticAnswer });
    CATEGORY_ORDER.forEach((categoryKey) => {
      (results[categoryKey]?.items || []).forEach((item) => flat.push({ categoryKey, item }));
    });
    return flat;
  }, [
    results,
    isSearching,
    visibleQuickLinks,
    visibleFavorites,
    suggestedSearches,
    suggestedPrompts,
    recentSearches,
    recentlyViewed,
    diagnosticAnswer,
  ]);

  const hasAnyResults = flatResults.length > 0;

  // Only in the empty-query browsing state (never over search results, same
  // as Recent/Suggested for You/Quick Links - requirement 6), only while
  // still eligible per diagnosticTipStore.js (not dismissed, not seen too
  // many times), and only when there's actually an Admin fillOnly prompt to
  // offer.
  const showDiagnosticTip = showDropdown && !isSearching && !!diagnosticTipEntry && diagnosticTipEligible;

  // Counts one "view" per dropdown-open session (not per keystroke/re-render)
  // - the ref resets only once the dropdown fully closes, so typing in and
  // out of the empty-query state while the dropdown stays open doesn't
  // double-count.
  useEffect(() => {
    if (showDiagnosticTip && !diagnosticTipCountedRef.current) {
      diagnosticTipCountedRef.current = true;
      recordDiagnosticTipView();
      setDiagnosticTipEligible(isDiagnosticTipEligible());
    }
    if (!showDropdown) {
      diagnosticTipCountedRef.current = false;
    }
  }, [showDiagnosticTip, showDropdown]);

  // Clicking the banner's own text fills the input with the example
  // template - reuses handleSelectPrompt (-> handleFillPromptTemplate)
  // exactly like the existing "Suggested Queries"/"Try asking a specific
  // question" rows already do, never a second copy of that logic.
  const handleSelectDiagnosticTip = () => {
    if (diagnosticTipEntry) handleSelectPrompt(diagnosticTipEntry);
  };

  // Picking an example FROM INSIDE the badge's popover is the only thing
  // that fills the search box - clicking the badge itself only opens the
  // popover (see the Popover wrapping it below), it never drops text into
  // the box on its own.
  const handleSelectDiagnosticBadgeExample = (entry) => {
    setDiagnosticBadgePopoverOpen(false);
    handleSelectPrompt(entry);
  };

  // Clicking a filter keyword in the help popover (e.g. "status:") teaches by
  // doing: it inserts "status:" into the box and immediately focuses it, so
  // the existing trailing-unfinished-token detection kicks in on its own and
  // shows the real value autocomplete (Pending/Approved/...) right away -
  // the admin never has to already know this syntax exists to discover it.
  const handleSelectHelpOperator = (key) => {
    setDiagnosticBadgePopoverOpen(false);
    const next = `${key}:`;
    setQuery(next);
    setIsOpen(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(next.length, next.length);
    });
  };

  // Clicking x hides it for this render and persists the dismissal so it
  // never shows again, regardless of remaining view count.
  const handleDismissDiagnosticTip = (e) => {
    e.stopPropagation();
    dismissDiagnosticTip();
    setDiagnosticTipEligible(false);
  };

  const handleSelectQuickLink = (link) => {
    const companySlug = getCompanySlug();
    const route = `/${companySlug}${link.route}`;
    recordRecent({ id: route, label: link.label, route, iconCategory: `quickLink:${link.label}` });
    history.push(route);
    setQuery("");
    closeDropdown();
  };

  // Same navigation as Quick Links, just a distinct iconCategory prefix so a
  // re-selected Recent row can look its icon back up in the right source list
  // (see resolveRecentIcon).
  const handleSelectSuggested = (entry) => {
    const companySlug = getCompanySlug();
    const route = `/${companySlug}${entry.route}`;
    recordRecent({ id: route, label: entry.label, route, iconCategory: `suggested:${entry.label}` });
    history.push(route);
    setQuery("");
    closeDropdown();
  };

  // Suggested Queries - a prompt with real params attaches them as plain
  // route query params the destination page already reads (see
  // searchPrompts.js's top comment for which prompts are parameterized vs
  // plain); one without params just navigates to the bare route, same as
  // Quick Links/Suggested for You above.
  // "Try asking" diagnostic-example prompts (see searchPrompts.js's
  // fillOnly/template entries in the Admin bucket) don't navigate anywhere -
  // they fill the input with an editable template and leave the dropdown
  // open so the admin can see live results as they swap in a name, instead
  // of recording a Recent entry / pushing a route like every other prompt.
  const handleFillPromptTemplate = (entry) => {
    // Defensive whitespace collapse before this ever reaches the input -
    // same reasoning as handleSelectOperatorSuggestion above, kept here too
    // so any double space introduced by this path (or a future template
    // entry) can't slip through either.
    const template = entry.template.replace(/\s{2,}/g, " ");
    setQuery(template);
    setActiveIndex(-1);
    setIsOpen(true);
    const placeholderStart = template.indexOf(TEMPLATE_PLACEHOLDER);
    const selectionStart = placeholderStart >= 0 ? placeholderStart : 0;
    const selectionEnd = placeholderStart >= 0 ? placeholderStart + TEMPLATE_PLACEHOLDER.length : template.length;
    // Focus/select has to happen after React re-renders the input with the
    // new value - a plain synchronous call here would select against the
    // input's still-stale value (or nothing, pre-mount race), so it's
    // deferred one frame rather than fought with refs/effects.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const handleSelectPrompt = (entry) => {
    if (entry.fillOnly && entry.template) {
      handleFillPromptTemplate(entry);
      return;
    }
    const companySlug = getCompanySlug();
    const route = entry.params
      ? `/${companySlug}${entry.route}?${new URLSearchParams(entry.params).toString()}`
      : `/${companySlug}${entry.route}`;
    recordRecent({ id: route, label: entry.label, route, iconCategory: `prompt:${entry.id}` });
    history.push(route);
    setQuery("");
    closeDropdown();
  };

  // Re-selecting a Recent row just re-navigates to its already-resolved route,
  // and bumps it back to the top of the list (dedup by route in recentSearchStore).
  const handleSelectRecent = (entry) => {
    recordRecent(entry);
    history.push(entry.route);
    setQuery("");
    closeDropdown();
  };

  // Re-selecting a Recently Viewed row re-navigates and bumps it back to the
  // top of its own list (dedup by id in recentlyViewedStore), same pattern
  // as handleSelectRecent above but against the separate store.
  const handleSelectRecentlyViewed = (entry) => {
    recordRecentlyViewed(entry);
    history.push(entry.route);
    setQuery("");
    closeDropdown();
  };

  const handleKeyDown = (e) => {
    // Operator-value autocomplete takes over Up/Down/Enter while it's showing
    // suggestions, so it never fights the main results list's own nav below.
    if (operatorSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setOperatorActiveIndex((prev) => (prev + 1) % operatorSuggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setOperatorActiveIndex((prev) => (prev <= 0 ? operatorSuggestions.length - 1 : prev - 1));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleSelectOperatorSuggestion(operatorSuggestions[operatorActiveIndex >= 0 ? operatorActiveIndex : 0]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOperatorActiveIndex(-1);
        return;
      }
    }
    if (!showDropdown || !hasAnyResults) {
      if (e.key === "Escape") closeDropdown();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? flatResults.length - 1 : prev - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      const picked = flatResults[activeIndex];
      if (picked.categoryKey === "quickLinks" || picked.categoryKey === "favorites") handleSelectQuickLink(picked.item);
      else if (picked.categoryKey === "suggested") handleSelectSuggested(picked.item);
      else if (picked.categoryKey === "prompts") handleSelectPrompt(picked.item);
      else if (picked.categoryKey === "recent") handleSelectRecent(picked.item);
      else if (picked.categoryKey === "recentlyViewed") handleSelectRecentlyViewed(picked.item);
      else if (picked.categoryKey === "diagnosticAnswer") handleSelectDiagnosticAnswer(picked.item);
      else handleSelect(picked.categoryKey, picked.item);
    } else if (e.key === "Escape") {
      closeDropdown();
    }
  };

  // Recent rows only stored a lookup key (iconCategory), never a React icon
  // node, since icons aren't serializable to localStorage - resolved back to
  // an actual icon/color here at render time.
  const resolveRecentIcon = (iconCategory) => {
    if (iconCategory?.startsWith("quickLink:")) {
      const label = iconCategory.slice("quickLink:".length);
      const link = QUICK_LINKS.find((l) => l.label === label);
      return link ? { icon: link.icon, color: link.color } : { icon: <HistoryOutlined />, color: "#6B7280" };
    }
    if (iconCategory?.startsWith("suggested:")) {
      const label = iconCategory.slice("suggested:".length);
      const entry = ALL_SUGGESTED_ENTRIES.find((e) => e.label === label);
      return entry ? { icon: entry.icon, color: entry.color } : { icon: <HistoryOutlined />, color: "#6B7280" };
    }
    if (iconCategory?.startsWith("prompt:")) {
      const id = iconCategory.slice("prompt:".length);
      const entry = ALL_PROMPT_ENTRIES.find((e) => e.id === id);
      return entry ? { icon: entry.icon, color: entry.color } : { icon: <HistoryOutlined />, color: "#6B7280" };
    }
    const cat = CATEGORY_CONFIG[iconCategory];
    return cat ? { icon: cat.icon, color: cat.color } : { icon: <HistoryOutlined />, color: "#6B7280" };
  };

  let rowCursor = -1;

  return (
    <div className={`global-search${showDropdown ? " global-search--expanded" : ""}`} ref={wrapperRef}>
      <div className="global-search__input-wrap">
        <SearchOutlined className="global-search__icon" />
        <input
          ref={inputRef}
          type="text"
          className="global-search__input"
          placeholder={PLACEHOLDER}
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
        />
        {query ? (
          <CloseCircleFilled
            className="global-search__clear"
            onClick={() => {
              setQuery("");
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
          />
        ) : !showDropdown ? (
          <>
            {/* Permanent (never expires, never dismissed) discoverability
                affordance for the Admin diagnostic FAQ - the earlier dropdown
                banner approach self-destructed after a few views, which is
                exactly wrong for a feature an admin needs to rediscover
                repeatedly. Lives in the same always-visible spot as the
                Ctrl K hint, so it's seen every time the box isn't focused,
                with no view-count/exhaustion logic at all. */}
            {diagnosticTipEntry && (
              <Popover
                open={diagnosticBadgePopoverOpen}
                onOpenChange={setDiagnosticBadgePopoverOpen}
                trigger="hover"
                mouseEnterDelay={0.15}
                mouseLeaveDelay={0.2}
                placement="bottomRight"
                overlayClassName="global-search__help-overlay"
                content={
                  <div className="global-search__help-popover">
                    <div className="global-search__help-section">
                      <div className="global-search__help-section-title">What you can search</div>
                      <div className="global-search__help-grid">
                        <div className="global-search__help-item">
                          <UserOutlined /> <span>Employees</span>
                        </div>
                        <div className="global-search__help-item">
                          <CalendarOutlined /> <span>Leave requests</span>
                        </div>
                        <div className="global-search__help-item">
                          <ClockCircleOutlined /> <span>Attendance</span>
                        </div>
                        <div className="global-search__help-item">
                          <GiftOutlined /> <span>Holidays</span>
                        </div>
                        <div className="global-search__help-item">
                          <FileProtectOutlined /> <span>Policies</span>
                        </div>
                        <div className="global-search__help-item">
                          <AppstoreOutlined /> <span>HRMS modules</span>
                        </div>
                        <div className="global-search__help-item">
                          <SettingOutlined /> <span>Settings</span>
                        </div>
                        <div className="global-search__help-item">
                          <ThunderboltOutlined /> <span>Quick actions</span>
                        </div>
                      </div>
                      <div className="global-search__help-hint">
                        <div className="global-search__help-hint-label">Filters - click to try:</div>
                        <div className="global-search__help-operator-row">
                          {["status", "department", "date", "designation", "shift", "branch"].map((key) => (
                            <button
                              type="button"
                              key={key}
                              className="global-search__help-operator-chip"
                              onClick={() => handleSelectHelpOperator(key)}
                            >
                              {key}:
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="global-search__help-divider" />
                    <div className="global-search__help-section">
                      <div className="global-search__help-section-title">💡 Or ask a question directly</div>
                      {diagnosticTipExamples.map((entry) => (
                        <button
                          type="button"
                          key={entry.id}
                          className="global-search__help-example"
                          onClick={() => handleSelectDiagnosticBadgeExample(entry)}
                        >
                          {entry.label}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                <button
                  type="button"
                  className="global-search__diagnostic-badge"
                  aria-label="Show what you can search for"
                >
                  💡
                </button>
              </Popover>
            )}
            <span className="global-search__shortcut">{isMac ? "⌘K" : "Ctrl K"}</span>
          </>
        ) : null}
      </div>

      {/* Everything below floats below the input as a single absolutely-
          positioned overlay (.global-search__overlay) - chips, the operator-
          value autocomplete, and the results dropdown stack on top of each
          other purely via normal document flow INSIDE that one floating
          box, instead of each being its own in-flow/absolute sibling of
          `.global-search`. Before this, the chips row was a normal in-flow
          sibling, so growing it (or the operator-suggestions box, which
          computed its own `top: calc(100% + 8px)` off `.global-search`'s
          OWN box height) added real height to `.global-search` itself - a
          flex child of the Topbar header row (height: 66px, align-items:
          center) - which visibly clipped/shifted the search box against the
          header and could misalign its siblings (Action Hub, icons, profile
          menu). Wrapping them together means `.global-search` itself never
          grows past its input-wrap's height no matter what's showing below
          it. */}
      {(operatorChips.length > 0 || operatorSuggestions.length > 0 || showDropdown) && (
        <div className="global-search__overlay">
      {operatorChips.length > 0 && (
        <div className="global-search__chips-row">
          <div className="global-search__chips">
            {operatorChips.map((chip) => (
              <span className="global-search__chip" key={chip.raw}>
                <span className="global-search__chip-label">
                  {chip.key}: {chip.value}
                </span>
                <CloseCircleFilled
                  className="global-search__chip-remove"
                  onClick={() => handleRemoveChip(chip)}
                />
              </span>
            ))}
          </div>
          {operatorChips.length > 1 && (
            <button type="button" className="global-search__chips-clear" onClick={handleClearAllChips}>
              Clear all
            </button>
          )}
        </div>
      )}

      {operatorSuggestions.length > 0 && (
        <div className="global-search__operator-suggestions">
          {operatorSuggestions.map((entry, index) => {
            const label = typeof entry === "string" ? entry : entry.label;
            const isActive = index === operatorActiveIndex;
            return (
              <button
                type="button"
                key={typeof entry === "string" ? entry : entry.value}
                className={`global-search__operator-suggestion${isActive ? " global-search__operator-suggestion--active" : ""}`}
                onMouseDown={(e) => {
                  // onMouseDown (not onClick) so this fires before the
                  // input's blur would otherwise dismiss the dropdown.
                  e.preventDefault();
                  handleSelectOperatorSuggestion(entry);
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {showDropdown && (
        <div className="global-search__dropdown">
          {showDiagnosticTip && (
            // Pinned ABOVE the scrollable area below (a non-scrolling sibling,
            // not `position: sticky` inside it) - `.global-search__dropdown`
            // itself has no overflow/scroll on it any more (that moved to
            // `.global-search__dropdown-scroll`, see GlobalSearch.css), so
            // this renders the instant the dropdown opens with zero scrolling,
            // every time, and isn't part of flatResults/activeIndex (it's a
            // banner, not a selectable/keyboard-navigable row).
            <div className="global-search__diagnostic-tip">
              <button
                type="button"
                className="global-search__diagnostic-tip-text"
                onClick={handleSelectDiagnosticTip}
              >
                💡 Tip: ask &quot;why is [name] absent&quot; for an instant answer
              </button>
              <button
                type="button"
                className="global-search__diagnostic-tip-dismiss"
                onClick={handleDismissDiagnosticTip}
                aria-label="Dismiss tip"
              >
                ×
              </button>
            </div>
          )}
          <div className="global-search__dropdown-scroll" onMouseMove={armMouse}>
          {!isSearching &&
            (() => {
              let quickRowCursor = -1;
              return (
                <>
                  {visibleFavorites.length > 0 && (
                    <div className="global-search__group">
                      <div className="global-search__group-header">
                        <span className="global-search__group-title">Favorites</span>
                      </div>
                      {visibleFavorites.map((link) => {
                        quickRowCursor += 1;
                        const idx = quickRowCursor;
                        const isActive = idx === activeIndex;
                        return (
                          <div className="global-search__row-wrap" key={link.label}>
                            <div
                              role="button"
                              tabIndex={0}
                              className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                              onMouseEnter={() => handleRowMouseEnter(idx)}
                              onClick={() => handleSelectQuickLink(link)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSelectQuickLink(link);
                              }}
                            >
                              <span className="global-search__row-icon" style={{ background: link.color }}>
                                {link.icon}
                              </span>
                              <span className="global-search__row-text">
                                <span className="global-search__row-label">{link.label}</span>
                              </span>
                            </div>
                            <button
                              type="button"
                              className="global-search__row-star global-search__row-star--active"
                              onClick={(e) => handleToggleFavorite(e, link.label)}
                              aria-label="Remove from favorites"
                            >
                              <StarFilled />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {recentSearches.length > 0 && (
                    <div className="global-search__group">
                      <div className="global-search__group-header">
                        <span className="global-search__group-title">Recent</span>
                      </div>
                      {recentSearches.map((entry) => {
                        quickRowCursor += 1;
                        const idx = quickRowCursor;
                        const isActive = idx === activeIndex;
                        const { icon, color } = resolveRecentIcon(entry.iconCategory);
                        return (
                          <button
                            type="button"
                            key={entry.id}
                            className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                            onMouseEnter={() => handleRowMouseEnter(idx)}
                            onClick={() => handleSelectRecent(entry)}
                          >
                            <span className="global-search__row-icon" style={{ background: color }}>
                              {icon}
                            </span>
                            <span className="global-search__row-text">
                              <span className="global-search__row-label">{entry.label}</span>
                              {entry.subtitle ? (
                                <span className="global-search__row-subtitle">{entry.subtitle}</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {recentlyViewed.length > 0 && (
                    <div className="global-search__group">
                      <div className="global-search__group-header">
                        <span className="global-search__group-title">Recently Viewed</span>
                        <button
                          type="button"
                          className="global-search__group-clear"
                          onClick={handleClearRecentlyViewed}
                        >
                          Clear
                        </button>
                      </div>
                      {recentlyViewed.map((entry) => {
                        quickRowCursor += 1;
                        const idx = quickRowCursor;
                        const isActive = idx === activeIndex;
                        const { icon, color } = resolveRecentIcon(entry.iconCategory);
                        return (
                          <button
                            type="button"
                            key={entry.id}
                            className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                            onMouseEnter={() => handleRowMouseEnter(idx)}
                            onClick={() => handleSelectRecentlyViewed(entry)}
                          >
                            <span className="global-search__row-icon" style={{ background: color }}>
                              {icon}
                            </span>
                            <span className="global-search__row-text">
                              <span className="global-search__row-label">{entry.label}</span>
                              {entry.subtitle ? (
                                <span className="global-search__row-subtitle">{entry.subtitle}</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {suggestedSearches.length > 0 && (
                    <div className="global-search__group">
                      <div className="global-search__group-header">
                        <span className="global-search__group-title">Suggested for You</span>
                      </div>
                      {suggestedSearches.map((entry) => {
                        quickRowCursor += 1;
                        const idx = quickRowCursor;
                        const isActive = idx === activeIndex;
                        return (
                          <button
                            type="button"
                            key={entry.label}
                            className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                            onMouseEnter={() => handleRowMouseEnter(idx)}
                            onClick={() => handleSelectSuggested(entry)}
                          >
                            <span className="global-search__row-icon" style={{ background: entry.color }}>
                              {entry.icon}
                            </span>
                            <span className="global-search__row-text">
                              <span className="global-search__row-label">{entry.label}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {suggestedPrompts.length > 0 && (
                    <div className="global-search__group">
                      <div className="global-search__group-header">
                        <span className="global-search__group-title">Suggested Queries</span>
                      </div>
                      {suggestedPrompts.map((entry) => {
                        quickRowCursor += 1;
                        const idx = quickRowCursor;
                        const isActive = idx === activeIndex;
                        return (
                          <button
                            type="button"
                            key={entry.id}
                            className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                            onMouseEnter={() => handleRowMouseEnter(idx)}
                            onClick={() => handleSelectPrompt(entry)}
                          >
                            <span className="global-search__row-icon" style={{ background: entry.color }}>
                              {entry.icon}
                            </span>
                            <span className="global-search__row-text">
                              <span className="global-search__row-label">{entry.label}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="global-search__group">
                    <div className="global-search__group-header">
                      <span className="global-search__group-title">Quick Links</span>
                    </div>
                    {visibleQuickLinks.map((link) => {
                      quickRowCursor += 1;
                      const idx = quickRowCursor;
                      const isActive = idx === activeIndex;
                      const isFav = favorites.includes(link.label);
                      return (
                        <div className="global-search__row-wrap" key={link.label}>
                          <div
                            role="button"
                            tabIndex={0}
                            className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                            onMouseEnter={() => handleRowMouseEnter(idx)}
                            onClick={() => handleSelectQuickLink(link)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSelectQuickLink(link);
                            }}
                          >
                            <span className="global-search__row-icon" style={{ background: link.color }}>
                              {link.icon}
                            </span>
                            <span className="global-search__row-text">
                              <span className="global-search__row-label">{link.label}</span>
                            </span>
                          </div>
                          <button
                            type="button"
                            className={`global-search__row-star${isFav ? " global-search__row-star--active" : ""}`}
                            onClick={(e) => handleToggleFavorite(e, link.label)}
                            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                          >
                            {isFav ? <StarFilled /> : <StarOutlined />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

          {isSearching && loading && (
            <div className="global-search__state">
              <Spin size="small" /> <span>Searching...</span>
            </div>
          )}

          {isSearching && !loading && error && trimmedQuery.length >= 2 && (
            <div className="global-search__state global-search__state--error">
              Couldn't load results right now. {error}
            </div>
          )}

          {isSearching && !loading && !hasAnyResults && (() => {
            // Admin-only teaching moment: a bare diagnostic-shaped word (e.g.
            // "why", "who") with no resolved employee yet is exactly when an
            // Admin needs to be shown the diagnostic FAQ feature exists, not
            // left at a generic dead-end. Reuses the SAME word-list regex
            // useGlobalSearch.js uses to decide whether to fire the
            // diagnostic API call, but deliberately WITHOUT that call's
            // DIAGNOSTIC_MIN_QUERY_LENGTH gate - a false positive here just
            // shows a few extra example rows (cheap), while a false negative
            // silently skips the teaching moment entirely (the one to avoid).
            const showDiagnosticExamples = getRoles(["Admin"]) && DIAGNOSTIC_QUERY_HINT.test(trimmedQuery);

            if (!showDiagnosticExamples) {
              return (
                <div className="global-search__state">
                  <div>No results for "{trimmedQuery}"</div>
                  <div className="global-search__state-hint">Try an employee name, leave type, or a setting like "punch".</div>
                </div>
              );
            }

            // Source of truth stays searchPrompts.js - just the Admin bucket's
            // fillOnly/template entries already computed into suggestedPrompts
            // above, never a second copy of the prompt list.
            const diagnosticExamplePrompts = suggestedPrompts.filter((entry) => entry.fillOnly);
            let exampleRowCursor = -1;
            return (
              <div className="global-search__group">
                <div className="global-search__group-header">
                  <span className="global-search__group-title">Try asking a specific question</span>
                </div>
                {diagnosticExamplePrompts.map((entry) => {
                  exampleRowCursor += 1;
                  const idx = exampleRowCursor;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      type="button"
                      key={entry.id}
                      className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                      onMouseEnter={() => handleRowMouseEnter(idx)}
                      onClick={() => handleSelectPrompt(entry)}
                    >
                      <span className="global-search__row-icon" style={{ background: entry.color }}>
                        {entry.icon}
                      </span>
                      <span className="global-search__row-text">
                        <span className="global-search__row-label">{entry.label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {isSearching && !loading && diagnosticAnswer && (() => {
            rowCursor += 1;
            const idx = rowCursor;
            const isActive = idx === activeIndex;
            return (
              <div className="global-search__group" key="diagnosticAnswer">
                <div className="global-search__group-header">
                  <span className="global-search__group-title">Answer</span>
                </div>
                <div
                  className={`global-search__row global-search__row--answer${isActive ? " global-search__row--active" : ""}`}
                  onMouseEnter={() => handleRowMouseEnter(idx)}
                >
                  <span className="global-search__row-icon" style={{ background: "#0EA5E9" }}>
                    <ThunderboltOutlined />
                  </span>
                  <span className="global-search__row-text">
                    <span className="global-search__row-label">{diagnosticAnswer.answer}</span>
                    {diagnosticAnswer.employee?._id ? (
                      <button
                        type="button"
                        className="global-search__row-subtitle global-search__row-link"
                        onClick={() => handleSelectDiagnosticAnswer(diagnosticAnswer)}
                      >
                        View {diagnosticAnswer.employee.name}&apos;s attendance
                      </button>
                    ) : null}
                  </span>
                </div>
              </div>
            );
          })()}

          {isSearching &&
            !loading &&
            hasAnyResults &&
            CATEGORY_ORDER.map((categoryKey) => {
              const category = results[categoryKey];
              const items = category?.items || [];
              if (items.length === 0) return null;
              const config = CATEGORY_CONFIG[categoryKey];

              return (
                <div className="global-search__group" key={categoryKey}>
                  <div className="global-search__group-header">
                    <span className="global-search__group-title">{config.title}</span>
                    <span className="global-search__group-count">{category.count ?? items.length}</span>
                  </div>
                  {items.map((item) => {
                    rowCursor += 1;
                    const idx = rowCursor;
                    const isActive = idx === activeIndex;
                    const subtitle = config.renderSubtitle?.(item);
                    const canHighlightSubtitle = SUBTITLE_HIGHLIGHT_CATEGORIES.includes(categoryKey);
                    return (
                      <button
                        type="button"
                        key={item._id || item.label}
                        className={`global-search__row${isActive ? " global-search__row--active" : ""}`}
                        onMouseEnter={() => handleRowMouseEnter(idx)}
                        onClick={() => handleSelect(categoryKey, item)}
                      >
                        <span className="global-search__row-icon" style={{ background: config.color }}>
                          {config.icon}
                        </span>
                        <span className="global-search__row-text">
                          <span className="global-search__row-label">
                            <HighlightText text={config.renderLabel(item)} query={queryFreeText} />
                          </span>
                          {subtitle ? (
                            <span className="global-search__row-subtitle">
                              {canHighlightSubtitle ? (
                                <HighlightText text={subtitle} query={queryFreeText} />
                              ) : (
                                subtitle
                              )}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
